import * as React from "react";
import "react-tagsinput/react-tagsinput.css";
import { Field, FieldProps, Formik, FormikProps } from "formik";
import {
  ApplicantTypeLiteralList,
  ApplicantTypeState,
} from "@graffer-inc/cloud-gov-core";
import { ApplicantTypeLiterals } from "../../../../application/domain/application/application-entity";
import { GroupShareSelectField } from "../../../molecules/group-share-select-field";
import {
  BasicProcedureDetailProps,
  ProcedureConsts,
} from "../../../../application/domain/procedures/procedure-entity";
import { TextField, Checkbox } from "@graffer-inc/console-design-kit";

type Values = BasicProcedureDetailProps & {
  name: string;
  applicantType: ApplicantTypeLiterals[];
  groupId?: string;
};

const validateName = (value: string) =>
  value.length === 0 || value.length > ProcedureConsts.MAX_NAME_LENGTH
    ? "手続き名称は1文字以上、64文字以下の値を入力してください。"
    : undefined;
const validateApplicantType = (value: string[]) =>
  value.length === 0 ? "1つ以上選択してください" : undefined;
const validateGroupId = (value?: string) =>
  !value || (value && value.length === 0)
    ? "選択またはキーワードで検索"
    : undefined;

// 現状は、Modal内で利用される想定の実装
export const ProcedureCreateForm: React.FunctionComponent<{
  initialValues: Values;
  orgId?: string;
  onSubmit: (values: Values) => void;
  onClose: () => void;
}> = ({ initialValues, orgId, onSubmit, onClose }) => {
  return (
    <Formik
      validateOnChange={true}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      {({
        values,
        handleSubmit,
        touched,
        setFieldValue,
      }: FormikProps<Values>) => (
        <form onSubmit={handleSubmit}>
          <section className="modal-card-body">
            <div className="field">
              <Field className="control" name="name">
                {({ field }: FieldProps) => (
                  <>
                    <TextField
                      label={"手続き名称"}
                      description={"手続き名称を入力してください。"}
                      placeholder={""}
                      onChange={(e) => setFieldValue("name", e.target.value)}
                      value={field.value}
                      errorMessages={validateName(field.value)}
                      required
                    />
                  </>
                )}
              </Field>
            </div>
            <div className="field">
              <Field className="control" name="applicantType">
                {({ field, form }: FieldProps) => (
                  <>
                    <Checkbox
                      required
                      label={"申請者種別"}
                      description={
                        "この設定は、後から変更することができません。"
                      }
                      options={[
                        ...ApplicantTypeLiteralList.map((v) => {
                          return {
                            value: v,
                            name: new ApplicantTypeState(v).matchType({
                              INDIVIDUAL: "個人",
                              CORPORATE: "法人",
                              UNINCORPORATED_GROUP: "団体",
                            }),
                          };
                        }),
                      ]}
                      values={field.value}
                      onChange={(e) => {
                        form.setFieldTouched("applicantType", true);
                        form.setFieldValue(
                          "applicantType",
                          field.value.indexOf(e.target.value) === -1
                            ? [...field.value, e.target.value]
                            : field.value.filter(
                                (value: ApplicantTypeLiterals) =>
                                  e.target.value !== value,
                              ),
                        );
                      }}
                    />
                    {touched.applicantType && (
                      <p className="is-size-7 has-text-danger">
                        {validateApplicantType(field.value)}
                      </p>
                    )}
                  </>
                )}
              </Field>
            </div>
            <div className="field">
              <Field className="control" name="groupId">
                {({ field, form }: FieldProps) => {
                  return (
                    <GroupShareSelectField
                      tenantId={orgId}
                      selectedGroupId={field.value}
                      onChange={(v) => {
                        form.setFieldValue("groupId", v);
                      }}
                    />
                  );
                }}
              </Field>
            </div>
          </section>
          <section className="modal-card-foot">
            <button
              type="button"
              className="button is-failure is-fullwidth"
              onClick={onClose}
            >
              キャンセルする
            </button>
            <button
              type="submit"
              className="button is-info is-fullwidth"
              disabled={
                !!validateName(values.name) ||
                !!validateApplicantType(values.applicantType) ||
                !!validateGroupId(values.groupId)
              }
            >
              登録する
            </button>
          </section>
        </form>
      )}
    </Formik>
  );
};
